<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert
			:message="showAlertMessage"
			:show="showAlert"
			:type="showAlertColor"
		></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- Header -->
			<div class="ex-header">
				<div class="ex-header-left">
					<div class="ex-icon-box">
						<v-icon size="18" color="white">mdi-weight-lifter</v-icon>
					</div>
					<div>
						<p class="ex-title">Liste des exercices</p>
						<p class="ex-subtitle">Gestion de la bibliothèque d'exercices</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="ex-search"
					color="blue darken-2"
				></v-text-field>

				<div class="d-flex gap-2 align-center ml-3">
					<v-dialog
						v-model="syncDialog"
						max-width="480px"
						scrollable
						v-if="selectedExercises.length > 0"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								outlined
								rounded
								small
								color="primary"
								v-bind="attrs"
								v-on="on"
								@click="getProtocols"
							>
								<v-icon left small>mdi-sync</v-icon>
								Sync.
							</v-btn>
						</template>
						<v-card rounded="xl" class="pa-2">
							<div class="sync-header">
								<div class="sync-icon-box">
									<v-icon size="16" color="white">mdi-sync</v-icon>
								</div>
								<div>
									<p class="sync-title">Synchroniser au(x) protocole(s)</p>
									<p class="sync-sub">{{ selectedExercises.length }} exercice(s) sélectionné(s)</p>
								</div>
								<v-spacer></v-spacer>
								<v-btn icon small @click="syncDialog = false">
									<v-icon color="#6b7280">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-divider></v-divider>
							<v-card-text class="pt-4">
								<v-autocomplete
									v-model="selectedProtocolId"
									:items="protocols"
									item-text="label"
									item-value="_id"
									label="Protocole cible"
									outlined
									dense
									rounded
									prepend-inner-icon="mdi-dumbbell"
									hide-details
									class="mb-4"
								></v-autocomplete>
								<div>
									<p class="field-label mb-2">Exercices sélectionnés :</p>
									<v-chip-group column>
										<v-chip
											v-for="ex in selectedExercises"
											:key="ex._id"
											small
											color="#fef2f2"
											text-color="#c62828"
										>
											{{ ex.label }}
										</v-chip>
									</v-chip-group>
								</div>
							</v-card-text>
							<v-card-actions class="pa-4 pt-2">
								<v-btn text color="error" rounded @click="syncDialog = false">Annuler</v-btn>
								<v-spacer></v-spacer>
								<v-btn depressed rounded class="sync-btn" @click="addSynchronization">
									Synchroniser
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>

					<v-dialog v-model="dialog" max-width="560px" scrollable v-if="!accessDenied">
						<template v-slot:activator="{ on, attrs }">
							<v-btn depressed rounded class="ex-add-btn" v-bind="attrs" v-on="on">
								<v-icon left small>mdi-plus</v-icon>
								Nouveau
							</v-btn>
						</template>
						<v-card rounded="xl" class="pa-2">
							<div class="ex-dialog-header">
								<div class="ex-icon-box-sm">
									<v-icon size="16" color="white">mdi-weight-lifter</v-icon>
								</div>
								<div>
									<p class="ex-dialog-title">{{ formTitle }}</p>
									<p class="ex-dialog-sub">Exercice de la bibliothèque</p>
								</div>
								<v-spacer></v-spacer>
								<v-btn icon small @click="close">
									<v-icon color="#6b7280">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-divider></v-divider>
							<v-card-text class="pt-5">
								<v-row>
									<v-col cols="12" sm="6">
										<v-text-field v-model="editedItem.label" label="Nom de l'exercice" outlined dense rounded hide-details></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field v-model="editedItem.videoLink" label="Lien de la vidéo" outlined dense rounded hide-details disabled></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<label class="field-label">Durée</label>
										<vue-number-input v-model="editedItem.duration" center size="small" :min="0" controls></vue-number-input>
									</v-col>
									<v-col cols="12" sm="6">
										<label class="field-label">Nombre de séries</label>
										<vue-number-input v-model="editedItem.series" center size="small" :min="0" controls></vue-number-input>
									</v-col>
									<v-col cols="12" sm="4">
										<label class="field-label">Temps d'exécution</label>
										<vue-number-input :disabled="editedItem.repetition" v-model="editedItem.executionDuration" center :min="0" size="small" controls></vue-number-input>
									</v-col>
									<v-col cols="12" sm="4">
										<label class="field-label">Temps de récupération</label>
										<vue-number-input v-model="editedItem.recoveryDuration" center :min="0" size="small" controls></vue-number-input>
									</v-col>
									<v-col cols="12" sm="4">
										<label class="field-label">Répétitions</label>
										<vue-number-input v-model="editedItem.repetition" center size="small" controls :min="0" @change="handleRepetitionInput"></vue-number-input>
									</v-col>
									<v-col cols="12">
										<label class="field-label">Description</label>
										<VueEditor v-model="editedItem.description" :editorToolbar="customToolbar" class="mt-2"></VueEditor>
									</v-col>
								</v-row>
							</v-card-text>
							<v-card-actions class="pa-4 pt-2">
								<v-btn text color="error" rounded @click="close">Annuler</v-btn>
								<v-spacer></v-spacer>
								<v-btn depressed rounded class="ex-save-btn" @click="save">Enregistrer</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</div>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="exercises"
				v-model="selectedExercises"
				:single-select="false"
				show-select
				item-key="_id"
				:loading="loadingTable"
				loading-text="Chargement en cours..."
				:search="search"
				class="ex-table"
				:footer-props="{
					'items-per-page-text': 'Lignes par page',
					'items-per-page-all-text': 'Tout',
				}"
			>
				<template v-slot:[`item.description`]="{ item }">
					<span class="ex-desc" v-html="item.description"></span>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-btn icon small @click="editItem(item)">
						<v-icon size="17" color="#374151">mdi-pencil</v-icon>
					</v-btn>
					<v-btn icon small @click="deleteItem(item)">
						<v-icon size="17" color="#c62828">mdi-delete</v-icon>
					</v-btn>
				</template>
				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- Delete dialog -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer cet exercice ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="dialogDelete = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteItemConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import service from "@/services";
export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		search: "",
		accessDenied: false,
		showAlert: false,
		showAlertMessage: "",
		showAlertColor: "",
		selectedExercises: [],
		selectedProtocolId: "",
		syncDialog: false,
		dialog: false,
		dialogDelete: false,
		loaderDialog: false,
		loadingTable: false,
		exercises: [],
		protocols: [],
		headers: [
			{ text: "Intitulé", align: "start", value: "label" },
			{ text: "Description", value: "description" },
			{ text: "Durée", value: "duration", align: "center" },
			{ text: "Tps exéc.", value: "executionDuration", align: "center" },
			{ text: "Tps récup.", value: "recoveryDuration", align: "center" },
			{ text: "Répét.", value: "repetition", align: "center" },
			{ text: "Séries", value: "series", align: "center" },
			{ text: "Actions", value: "actions", sortable: false },
		],
		editedItem: { label: "", description: "", duration: 0, executionDuration: 0, recoveryDuration: 0, series: 0, repetition: 0, videoLink: "", imageLink: "", _id: "" },
		defaultItem: { label: "", description: "", duration: 0, executionDuration: 0, recoveryDuration: 0, series: 0, repetition: 0, videoLink: "", imageLink: "", _id: "" },
		customToolbar: [[{ header: [false, 1, 2, 3, 4, 5, 6] }], ["bold", "italic", "underline"], [{ color: [] }], [{ list: "ordered" }, { list: "bullet" }]],
		editedIndex: -1,
	}),
	computed: {
		formTitle() {
			return this.editedIndex === -1 ? "Nouvel exercice" : "Modifier l'exercice";
		},
	},
	watch: {
		dialog(val) { val || this.close(); },
		dialogDelete(val) { val || this.closeDelete(); },
	},
	created() { this.initialize(); },
	methods: {
		handleRepetitionInput() {
			if (this.editedItem.repetition != 0) this.editedItem.executionDuration = 0;
		},
		initialize() {
			this.loadingTable = true;
			service.getExercises().then((r) => { this.exercises = r.data?.exercises || r.data || []; this.loadingTable = false; }).catch((e) => { console.log(e); this.accessDenied = true; this.loadingTable = false; });
		},
		manageAlert(display, type, message) {
			this.showAlert = display;
			this.showAlertColor = type;
			this.showAlertMessage = message;
		},
		save() {
			if (this.editedIndex > -1) {
				this.loaderDialog = true;
				service.updateExercise(this.editedItem._id, this.editedItem).then((r) => { this.loaderDialog = false; this.close(); this.manageAlert(true, "success", r.message); }).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e.message); });
			} else {
				this.loaderDialog = true;
				service.addExercise(this.editedItem).then((r) => { this.loaderDialog = false; this.manageAlert(true, "success", r.message); this.close(); this.initialize(); }).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e.message); });
			}
		},
		getProtocols() {
			this.loaderDialog = true;
			service.getProtocols().then((r) => { this.protocols = r.data.data; this.loaderDialog = false; }).catch((e) => { console.log(e); this.loaderDialog = false; });
		},
		addSynchronization() {
			this.loaderDialog = true;
			const ids = this.selectedExercises.map((e) => e._id);
			service.syncExerciseWithProtocol(ids, this.selectedProtocolId).then((r) => { this.selectedExercises = []; this.selectedProtocolId = ""; this.loaderDialog = false; this.syncDialog = false; this.manageAlert(true, "success", r.message); }).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e.message); });
		},
		editItem(item) { this.editedIndex = this.exercises.indexOf(item); this.editedItem = Object.assign({}, item); this.dialog = true; },
		deleteItem(item) { this.editedIndex = this.exercises.indexOf(item); this.editedItem = Object.assign({}, item); this.dialogDelete = true; },
		deleteItemConfirm() {
			this.loaderDialog = true;
			service.deleteExercise(this.editedItem._id).then((r) => { this.loaderDialog = false; this.manageAlert(true, "success", r.message); this.closeDelete(); }).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e.message); });
		},
		close() { this.dialog = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		closeDelete() { this.dialogDelete = false; this.$nextTick(() => { this.initialize(); this.editedIndex = -1; }); },
	},
};
</script>

<style scoped>
.ex-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.ex-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.ex-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ex-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.ex-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.ex-search { max-width: 220px; }
.ex-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.ex-table { font-family: Montserrat, sans-serif; }
.ex-desc { font-size: 0.78rem; color: #6b7280; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ex-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.ex-icon-box-sm { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.ex-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.ex-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.field-label { font-size: 0.75rem; font-weight: 600; color: #6b7280; font-family: Montserrat, sans-serif; }
.ex-save-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.sync-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.sync-icon-box { width: 34px; height: 34px; border-radius: 8px; background: #1565c0; display: flex; align-items: center; justify-content: center; }
.sync-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.sync-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.sync-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.del-confirm-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
</style>

